import { useState, useRef, useEffect } from 'react'
import Modal from '../shared/Modal'
import { MetricWithEntries, DateColumn } from '../../types'
import { QueuedSave } from './useSaveQueue'

export interface EntryDetailUpdate {
  columnKey: string
  context: string | null
  variableTarget: number | null
}

interface Props {
  isOpen: boolean
  onClose: () => void
  metric: MetricWithEntries | null
  dateColumns: DateColumn[]
  teamId: number
  // The page's serialized save queue — shared with the inline cells so a
  // notes save can never cancel a cell save that is still in flight.
  enqueue: (item: QueuedSave) => void
  // Applied to the page's local metric mirror once every changed period has
  // been persisted, so the cells' notes indicators update without a reload.
  onSaved: (metricId: number, updates: EntryDetailUpdate[]) => void
}

interface RowState {
  context: string
  useVariableTarget: boolean
  variableTarget: string
}

function initialRows(metric: MetricWithEntries | null, dateColumns: DateColumn[]): Record<string, RowState> {
  const rows: Record<string, RowState> = {}
  if (!metric) return rows
  dateColumns.forEach(col => {
    const entry = metric.entries[col.key]
    rows[col.key] = {
      context: entry?.context || entry?.notes || '',
      useVariableTarget: entry?.variable_target != null,
      variableTarget: entry?.variable_target != null ? String(entry.variable_target) : '',
    }
  })
  return rows
}

/**
 * Per-metric editor for Context/Notes and the per-period variable target
 * across the displayed periods (task #596). These used to live in the
 * Check-In popup that opened on a cell click; cells are now inline-editable,
 * so this moved behind the row's three-dot menu.
 *
 * Saves go through the same team metric-entry create endpoint the inline
 * cells use (it upserts by recorded date). The metric passed in comes from
 * the page's LOCAL mirror, so it stays in step with whatever was just
 * typed inline — saving notes can never wipe it.
 */
export default function EntryNotesModal({ isOpen, onClose, metric, dateColumns, teamId, enqueue, onSaved }: Props) {
  const [rows, setRows] = useState<Record<string, RowState>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const baselineRef = useRef<Record<string, RowState>>({})

  // Initialize only on closed→open (keyed by metric id), never on prop
  // identity — a background refresh mid-edit must not reset the form.
  const openKey = isOpen && metric ? String(metric.id) : null
  const prevOpenKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (openKey === prevOpenKeyRef.current) return
    prevOpenKeyRef.current = openKey
    if (!openKey) return
    const next = initialRows(metric, dateColumns)
    baselineRef.current = next
    setRows(next)
    setError(null)
    setSaving(false)
  }, [openKey, metric, dateColumns])

  if (!metric) return null

  const setRow = (colKey: string, patch: Partial<RowState>) => {
    setRows(prev => ({ ...prev, [colKey]: { ...prev[colKey], ...patch } }))
  }

  const changedColumns = dateColumns.filter(col => {
    const before = baselineRef.current[col.key]
    const now = rows[col.key]
    if (!before || !now) return false
    const beforeTarget = before.useVariableTarget ? before.variableTarget : ''
    const nowTarget = now.useVariableTarget ? now.variableTarget : ''
    return before.context !== now.context || beforeTarget !== nowTarget
  })

  const handleSave = () => {
    if (saving) return
    if (changedColumns.length === 0) {
      onClose()
      return
    }
    setSaving(true)
    setError(null)

    const applied: EntryDetailUpdate[] = []

    // One request per changed period, run in sequence so a failure stops
    // cleanly and reports which period could not be saved.
    const saveAt = (index: number) => {
      if (index >= changedColumns.length) {
        setSaving(false)
        onSaved(metric.id, applied)
        onClose()
        return
      }
      const col = changedColumns[index]
      const row = rows[col.key]
      const context = row.context.trim() === '' ? null : row.context
      const variableTarget = row.useVariableTarget && row.variableTarget !== ''
        ? parseFloat(row.variableTarget)
        : null
      const existing = metric.entries[col.key]

      enqueue({
        // Same key format the inline cells use, so this folds into a value
        // save still waiting on that cell instead of racing it.
        key: `${metric.id}:${col.key}`,
        url: `/teams/${teamId}/scoreboard/metrics/${metric.id}/entries${typeof window !== 'undefined' ? window.location.search : ''}`,
        mergeKey: 'metric_entry',
        body: {
          metric_entry: {
            // Target the existing entry's own recorded date so the upsert
            // updates it rather than inserting a duplicate for the period.
            recorded_at: existing?.recorded_at || col.start_date,
            // Deliberately no `value`: the upsert leaves attributes it isn't
            // given alone, so omitting it can neither blank out a number the
            // user just typed nor make this request "confirm" a value it
            // never actually carried.
            context,
            variable_target: variableTarget,
          },
        },
        onSuccess: () => {
          applied.push({ columnKey: col.key, context, variableTarget })
          saveAt(index + 1)
        },
        onError: (detail) => {
          setSaving(false)
          setError(`Could not save ${col.label}${detail ? `: ${detail}` : '.'}`)
          if (applied.length > 0) onSaved(metric.id, applied)
        },
      })
    }

    saveAt(0)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Notes & Targets">
      <div className="space-y-4">
        <div>
          <p className="text-sm font-medium text-text-primary">{metric.name}</p>
          <p className="text-sm text-text-secondary">
            Context and a per-period target for the periods currently shown.
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-[#ED1C24]/30 bg-[#ED1C24]/10 px-3 py-2 text-sm text-[#ED1C24]" role="alert">
            {error}
          </div>
        )}

        <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-1">
          {dateColumns.map(col => {
            const row = rows[col.key]
            if (!row) return null
            return (
              <div key={col.key} className="border border-stone/20 rounded-lg p-3 space-y-2">
                <p className="text-sm font-semibold text-text-primary">{col.label}</p>
                <label className="block">
                  <span className="block text-sm text-text-secondary mb-1">Context / Notes</span>
                  <textarea
                    value={row.context}
                    onChange={e => setRow(col.key, { context: e.target.value })}
                    rows={2}
                    aria-label={`Context for ${col.label}`}
                    placeholder="Add context or notes for this period..."
                    className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 focus:border-pinnacle-red resize-none"
                  />
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={row.useVariableTarget}
                    onChange={e => setRow(col.key, { useVariableTarget: e.target.checked })}
                    className="w-4 h-4 rounded border-stone/30 text-pinnacle-red focus:ring-pinnacle-red/30"
                  />
                  <span className="text-sm text-text-primary">Use variable target for this period</span>
                </label>
                {row.useVariableTarget && (
                  <input
                    type="number"
                    step="any"
                    value={row.variableTarget}
                    onChange={e => setRow(col.key, { variableTarget: e.target.value })}
                    aria-label={`Variable target for ${col.label}`}
                    placeholder="Variable target"
                    className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 focus:border-pinnacle-red"
                  />
                )}
              </div>
            )
          })}
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-text-primary hover:bg-stone/10 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-semibold text-white bg-pinnacle-red hover:bg-pinnacle-red/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
